import { Controller, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
  type ComboboxOption,
  MultiSelectCombobox,
} from "../../../../../InfoHub/Components/Form/Components/MultiSelectCombobox";
import { TraderDetailsProfileField } from "../TraderDetailsProfileField";
import type { SelectOption, TraderDetailsStepOneFormValues } from "../TraderDetailsStepOne/types";

interface TraderDetailsEligibleAggregatorsFieldProps {
  control: Control<TraderDetailsStepOneFormValues>;
  label: string;
  tooltip?: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  options: ComboboxOption[];
  isLoading?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
}

const toComboboxOption = (option: SelectOption): ComboboxOption => ({
  value: option.value,
  label: option.label,
});

export const TraderDetailsEligibleAggregatorsField = ({
  control,
  label,
  tooltip,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  options,
  isLoading = false,
  isDisabled = false,
  errorMessage,
}: TraderDetailsEligibleAggregatorsFieldProps) => {
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name="eligible_aggregators"
      render={({ field }) => {
        const selected: SelectOption[] = field.value ?? [];

        const removeAggregator = (value: SelectOption["value"]) => {
          field.onChange(selected.filter(option => option.value !== value));
        };

        return (
          <TraderDetailsProfileField
            id="eligible-aggregators-select"
            label={label}
            tooltip={tooltip}
            errorMessage={errorMessage}
          >
            <MultiSelectCombobox
              id="eligible-aggregators-select"
              options={options}
              value={selected.map(toComboboxOption)}
              placeholder={placeholder}
              searchPlaceholder={searchPlaceholder}
              emptyMessage={emptyMessage}
              errorMessage={errorMessage}
              isDisabled={isDisabled}
              isLoading={isLoading}
              onChange={nextValue => field.onChange(nextValue)}
            />
            {selected.length > 0 && (
              <ul className={cn("flex flex-wrap gap-2", isDisabled && "opacity-60")}>
                {selected.map(option => (
                  <li key={option.value}>
                    <Badge variant="secondary" className="gap-1 pr-1">
                      <span className="truncate">{option.label}</span>
                      <button
                        type="button"
                        disabled={isDisabled}
                        aria-label={t("traderDetails.form.eligibleAggregatorsRemove", {
                          name: option.label,
                        })}
                        className="flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
                        onClick={() => removeAggregator(option.value)}
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </TraderDetailsProfileField>
        );
      }}
    />
  );
};
